import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import { PASTOR_NAME, SITE_NAME } from "@/lib/data";

const LINKS = [
  { label: "Home", to: "/", testid: "nav-home" },
  { label: "Live Stream", to: "/live", testid: "nav-live" },
  { label: "Community Chat", to: "/chat", testid: "nav-chat" },
  { label: "Contact", to: "/contact", testid: "nav-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-sand/70 border-b border-black/5"
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex flex-col leading-none"
          onClick={() => setOpen(false)}
        >
          <span className="font-heading text-xl md:text-[22px] tracking-tight text-ink">
            Chief Apostle Leonard Lucas Jr.
          </span>
          <span className="overline text-terracotta mt-0.5">Masterclass</span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`link-underline font-body text-[15px] transition-colors ${
                pathname === l.to ? "text-terracotta" : "text-ink hover:text-terracotta"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href="/#newsletter"
            data-testid="nav-newsletter-cta"
            className="ml-2 inline-flex items-center rounded-full bg-forest px-5 py-2.5 text-sand text-sm font-medium transition-all hover:bg-forest-hover hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-terracotta"
          >
            Join the Circle
          </a>
          <a
            href="https://checkout.square.site/merchant/ML5VXMD5MXAKZ/checkout/HK3JDRKPT2XLIJCEBCTIRWVN"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-give-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2.5 text-white text-sm font-medium transition-all hover:bg-terracotta-hover hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-forest"
          >
            <Heart size={15} fill="currentColor" /> Give Now
          </a>
        </div>

        <button
          className="md:hidden text-ink p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-sand border-t border-black/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`${l.testid}-mobile`}
                  className="py-3 font-body text-lg text-ink border-b border-black/5"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href="/#newsletter"
                onClick={() => setOpen(false)}
                className="mt-3 text-center rounded-full bg-forest px-5 py-3 text-sand font-medium"
              >
                Join the Circle
              </a>
              <a
                href="https://checkout.square.site/merchant/ML5VXMD5MXAKZ/checkout/HK3JDRKPT2XLIJCEBCTIRWVN"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-give-cta-mobile"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-terracotta px-5 py-3 text-white font-medium"
              >
                <Heart size={16} fill="currentColor" /> Give Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-charcoal text-sand/80 grain" data-testid="main-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid gap-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <h3 className="font-heading text-3xl text-sand">{SITE_NAME}</h3>
        <p className="mt-4 font-body text-sm leading-relaxed max-w-sm text-sand/70">
          Igniting the body of Christ worldwide — bridging generations through apostolic
          fire, spiritual fatherhood, and the integrity of Kingdom leadership.
        </p>
      </div>
      <div className="md:col-span-3">
        <p className="overline text-terracotta">Explore</p>
        <div className="mt-4 flex flex-col gap-2 font-body text-sm">
          <Link to="/live" className="link-underline w-fit">Live Stream</Link>
          <Link to="/chat" className="link-underline w-fit">Community Chat</Link>
          <Link to="/contact" className="link-underline w-fit">Contact</Link>
        </div>
      </div>
      <div className="md:col-span-4">
        <p className="overline text-terracotta">A Word for the Road</p>
        <p className="mt-4 font-heading italic text-2xl text-sand leading-snug">
          "Come as you are. You are already welcome here."
        </p>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 text-xs text-sand/50 font-body">
        © {new Date().getFullYear()} {SITE_NAME}. All rights reserved.
      </div>
    </div>
  </footer>
);
